import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trophy, TrendingUp, TrendingDown, Minus, Share2, Flame, Swords } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { getDecimalLevel } from "@/lib/padelLevels";

export default function MatchHighlights() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/matches/${id}/highlights`).then(r => setData(r.data)).catch(() => {
      toast.error("Highlights not available");
      navigate("/matches");
    }).finally(() => setLoading(false));
  }, [id, navigate]);

  const handleShare = () => {
    if (!data) return;
    if (navigator.share) {
      navigator.share({ title: "Match Result — Padel Moris", text: data.share_text, url: window.location.href });
    } else {
      navigator.clipboard.writeText(data.share_text + "\n" + window.location.href);
      toast.success("Copied to clipboard!");
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-coral rounded-full border-t-transparent" /></div>;
  if (!data) return null;

  return (
    <div className="max-w-lg mx-auto px-4 py-8 sm:py-12">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-6">
        {/* Score Card */}
        <Card className="border-coral/20 bg-gradient-to-br from-coral/5 to-transparent overflow-hidden" data-testid="highlights-card">
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Trophy size={20} className="text-golden" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Match Result</span>
            </div>
            <p className="text-6xl font-black tracking-tight mb-2" data-testid="highlights-score">{data.score}</p>
            <p className="text-sm text-muted-foreground mb-1">{data.club_name}</p>
            <p className="text-xs text-muted-foreground">{data.date} · {data.time} · {data.mode}</p>
          </CardContent>
        </Card>

        {/* Player Stats */}
        <div className="space-y-3">
          {data.players?.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }}>
              <Card className="border-border/50" data-testid="highlight-player-card">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-bold">
                      {p.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-bold text-sm">{p.name}</p>
                      <p className="text-xs text-muted-foreground">Lv. {getDecimalLevel(p.elo_rating)} · {p.wins}W/{p.matches_played - p.wins}L</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.win_streak > 0 && (
                      <Badge className="rounded-full text-[10px] bg-coral/10 text-coral border-coral/30 gap-0.5">
                        <Flame size={10} /> {p.win_streak}
                      </Badge>
                    )}
                    {p.elo_change !== 0 && (
                      <Badge className={`rounded-full text-xs gap-0.5 ${
                        p.elo_change > 0 ? "bg-green-500/10 text-green-500 border-green-500/30" : "bg-destructive/10 text-destructive border-destructive/30"
                      }`}>
                        {p.elo_change > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                        {p.elo_change > 0 ? "+" : ""}{Math.round(p.elo_change)}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Share Button */}
        <Button onClick={handleShare} className="w-full rounded-full bg-coral hover:bg-coral-600 text-white gap-2 h-12 text-base" data-testid="share-highlights-btn">
          <Share2 size={18} /> Share Result
        </Button>
      </motion.div>
    </div>
  );
}
